import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Mail, Building2, MessageSquare, CheckCircle } from 'lucide-react';
import { useTheme } from '../App';
interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ContactModal({
  isOpen,
  onClose
}: ContactModalProps) {
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [formData, setFormData] = useState({
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Send email using a serverless function or email service
      const response = await fetch('https://formsubmit.co/ajax/javdamico@gmail.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          company: formData.company || 'Not provided',
          message: formData.message,
          _subject: formData.company ? `New message from ${formData.company}` : 'New Contact Form Message',
          _template: 'table'
        })
      });
      if (response.ok) {
        setSubmitStatus('success');
        // Reset form after 2 seconds
        setTimeout(() => {
          setFormData({
            email: '',
            company: '',
            message: ''
          });
          setSubmitStatus('idle');
          onClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <AnimatePresence data-id="element-38">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/90 backdrop-blur-md z-50" data-id="element-39" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-40">
            <div onClick={e => e.stopPropagation()} className={`w-full max-w-2xl rounded-2xl border overflow-hidden ${isDark ? 'bg-neutral-950 border-white/10' : 'bg-white border-black/10'}`} data-id="element-41">
              {/* Header */}
              <div className="p-8 border-b" style={{
            borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
          }} data-id="element-42">
                <div className="flex items-center justify-between" data-id="element-43">
                  <div data-id="element-44">
                    <h2 className={`text-4xl font-black tracking-tighter ${isDark ? 'text-white' : 'text-black'}`} data-id="element-45">
                      Let's Talk
                    </h2>
                    <p className="mt-2 text-sm" style={{
                  color: isDark ? '#666' : '#999'
                }} data-id="element-46">
                      I'll get back to you within 24 hours
                    </p>
                  </div>
                  <button onClick={onClose} className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-300 hover:opacity-70 ${isDark ? 'border-white/10 hover:border-white/20' : 'border-black/10 hover:border-black/20'}`} aria-label="Close" data-id="element-47">
                    <X className="w-5 h-5" style={{
                  color: isDark ? '#999' : '#666'
                }} data-id="element-48" />
                  </button>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-8 space-y-6" data-id="element-49">
                {/* Email */}
                <div data-id="element-50">
                  <label className="block text-xs uppercase tracking-wider font-medium mb-3" style={{
                color: isDark ? '#666' : '#999'
              }} data-id="element-51">
                    Email Address *
                  </label>
                  <div className="relative" data-id="element-52">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{
                  color: isDark ? '#444' : '#ccc'
                }} data-id="element-53" />
                    <input type="email" value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} required className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 transition-all duration-300 focus:outline-none ${isDark ? 'bg-black border-white/5 text-white focus:border-white/20 placeholder:text-neutral-700' : 'bg-neutral-50 border-black/5 text-black focus:border-black/20 placeholder:text-neutral-400'}`} placeholder="your@email.com" data-id="element-54" />
                  </div>
                </div>

                {/* Company (Optional) */}
                <div data-id="element-55">
                  <label className="block text-xs uppercase tracking-wider font-medium mb-3" style={{
                color: isDark ? '#666' : '#999'
              }} data-id="element-56">
                    Company{' '}
                    <span className="text-xs opacity-50" data-id="element-57">(Optional)</span>
                  </label>
                  <div className="relative" data-id="element-58">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{
                  color: isDark ? '#444' : '#ccc'
                }} data-id="element-59" />
                    <input type="text" value={formData.company} onChange={e => setFormData({
                  ...formData,
                  company: e.target.value
                })} className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 transition-all duration-300 focus:outline-none ${isDark ? 'bg-black border-white/5 text-white focus:border-white/20 placeholder:text-neutral-700' : 'bg-neutral-50 border-black/5 text-black focus:border-black/20 placeholder:text-neutral-400'}`} placeholder="Your Company" data-id="element-60" />
                  </div>
                </div>

                {/* Message */}
                <div data-id="element-61">
                  <label className="block text-xs uppercase tracking-wider font-medium mb-3" style={{
                color: isDark ? '#666' : '#999'
              }} data-id="element-62">
                    Message *
                  </label>
                  <div className="relative" data-id="element-63">
                    <MessageSquare className="absolute left-4 top-4 w-5 h-5" style={{
                  color: isDark ? '#444' : '#ccc'
                }} data-id="element-64" />
                    <textarea value={formData.message} onChange={e => setFormData({
                  ...formData,
                  message: e.target.value
                })} required rows={6} className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 transition-all duration-300 focus:outline-none resize-none ${isDark ? 'bg-black border-white/5 text-white focus:border-white/20 placeholder:text-neutral-700' : 'bg-neutral-50 border-black/5 text-black focus:border-black/20 placeholder:text-neutral-400'}`} placeholder="Tell me about your project..." data-id="element-65" />
                  </div>
                </div>

                {/* Submit Status */}
                <AnimatePresence mode="wait" data-id="element-66">
                  {submitStatus === 'success' && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="p-4 rounded-xl bg-green-500/10 border-2 border-green-500/20 flex items-center gap-3" data-id="element-67">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-68" />
                      <p className="text-sm text-green-500 font-medium" data-id="element-69">
                        Message sent successfully! I'll get back to you soon.
                      </p>
                    </motion.div>}

                  {submitStatus === 'error' && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="p-4 rounded-xl bg-red-500/10 border-2 border-red-500/20" data-id="element-70">
                      <p className="text-sm text-red-500 font-medium" data-id="element-71">
                        Something went wrong. Please try again or email me
                        directly at javdamico@gmail.com
                      </p>
                    </motion.div>}
                </AnimatePresence>

                {/* Submit Button */}
                <div className="flex gap-4 pt-4" data-id="element-72">
                  <button type="submit" disabled={isSubmitting} className={`flex-1 py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 ${isDark ? 'bg-white text-black' : 'bg-black text-white'}`} data-id="element-73">
                    {isSubmitting ? <>
                        <motion.div animate={{
                    rotate: 360
                  }} transition={{
                    duration: 1,
                    repeat: Infinity,
                    ease: 'linear'
                  }} data-id="element-74">
                          <Send className="w-5 h-5" data-id="element-75" />
                        </motion.div>
                        Sending...
                      </> : <>
                        Send Message
                        <Send className="w-5 h-5" data-id="element-76" />
                      </>}
                  </button>
                  <button type="button" onClick={onClose} className={`px-8 py-4 rounded-xl font-bold border-2 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] ${isDark ? 'border-white/10 text-white hover:border-white/20' : 'border-black/10 text-black hover:border-black/20'}`} data-id="element-77">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}